import DOMPurify from 'dompurify'

/**
 * Saniteert de HTML die citeproc per citatie of literatuurregel oplevert, vóór die via
 * `dangerouslySetInnerHTML` in de DOM komt (`Referentielijst.tsx`).
 *
 * Letterlijk overgenomen uit CitatenPOC's `sanitizeHtml.ts` (bijlage C.2). Citeproc escapet zijn
 * tekstvelden weliswaar zelf (`<`, `>`, `&`), maar wat erin gaat is brondata die de invuller zelf
 * intypt (R-50): verdediging in de diepte, ook als citeproc's escaping ooit verandert. Alleen de
 * opmaaktags die citeproc legitiem produceert.
 */

/** Tags die citeproc gebruikt voor opmaak (cursief, super/subscript, links, wrappers). */
const ALLOWED_TAGS = ['i', 'em', 'b', 'strong', 'u', 's', 'sup', 'sub', 'small', 'cite', 'span', 'a', 'div', 'br']

/** Attributen die citeproc op die tags zet (CSS-klassen, hangende inspringing, links). */
const ALLOWED_ATTR = ['class', 'style', 'href', 'rel', 'target', 'title', 'lang', 'dir']

/** Alleen veilige linkschema's; blokkeert onder meer `javascript:`. */
const ALLOWED_URI_REGEXP = /^(?:https?|mailto|tel|ftp):/i

export function sanitizeCitationHtml(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP })
}

const TAG =
  /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z][a-zA-Z0-9-]*(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*\/?>/g
const ATTRIBUTE = /([a-zA-Z][a-zA-Z0-9-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g
/** Knopen waarvan ook de inhoud weg moet, zoals DOMPurify dat doet. */
const DROP_WITH_CONTENT = /<(script|style|iframe|object|embed|noscript|template)\b[\s\S]*?<\/\1\s*>/gi

/**
 * Dezelfde sanitering zonder DOM, voor de pod (R-50, R-202).
 *
 * De pod draait in Node en legt bij het bevriezen de literatuurregels vast voor het bericht aan
 * het processysteem (`exportSources.ts`). DOMPurify heeft daar geen DOM, en een DOM inladen voor
 * één regel tekst is meer gereedschap dan het werk vraagt. Deze functie bouwt de HTML opnieuw op
 * uit dezelfde lijsten hierboven: een toegestane tag met alleen toegestane attributen blijft, elke
 * andere tag verdwijnt, en een losse `<` of `>` in de tekst wordt ontsnapt. Wat ze niet herkent,
 * wordt dus tekst en nooit opmaak.
 */
export function sanitizeCitationHtmlWithoutDom(html: string): string {
  const source = html.replace(DROP_WITH_CONTENT, '')
  let out = ''
  let at = 0
  for (const match of source.matchAll(TAG)) {
    out += escapeLoose(source.slice(at, match.index))
    at = match.index + match[0].length
    const [, closing, name, attributes] = match
    const tag = (name ?? '').toLowerCase()
    if (!ALLOWED_TAGS.includes(tag)) continue
    if (closing === '/') {
      out += `</${tag}>`
      continue
    }
    let kept = ''
    for (const attribute of (attributes ?? '').matchAll(ATTRIBUTE)) {
      const attributeName = (attribute[1] ?? '').toLowerCase()
      const value = attribute[2] ?? attribute[3] ?? attribute[4] ?? ''
      if (!ALLOWED_ATTR.includes(attributeName)) continue
      if (attributeName === 'href' && !ALLOWED_URI_REGEXP.test(value.trim())) continue
      kept += ` ${attributeName}="${value.replace(/&(?![a-zA-Z0-9#]+;)/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')}"`
    }
    out += `<${tag}${kept}>`
  }
  return out + escapeLoose(source.slice(at))
}

/** Tekst tussen tags: een losse `<`, `>` of `&` zonder entiteit wordt ontsnapt, een entiteit blijft. */
function escapeLoose(text: string): string {
  return text.replace(/&(?![a-zA-Z0-9#]+;)/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
